import React, { useState, createContext, useContext } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { motion } from 'framer-motion';
// Create context for the accordion
type AccordionContextValue = {
  value: string | null;
  onValueChange: (value: string | null) => void;
  type: 'single' | 'multiple';
};
const AccordionContext = createContext<AccordionContextValue | undefined>(undefined);
// Accordion component
interface AccordionProps {
  children: React.ReactNode;
  type: 'single' | 'multiple';
  collapsible?: boolean;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string | null) => void;
  className?: string;
}
export function Accordion({
  children,
  type = 'single',
  collapsible = false,
  defaultValue,
  value,
  onValueChange,
  className = ''
}: AccordionProps) {
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue || null);
  const handleValueChange = (newValue: string | null) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };
  const currentValue = value !== undefined ? value : internalValue;
  const contextValue: AccordionContextValue = {
    value: currentValue,
    onValueChange: newValue => {
      if (newValue === currentValue && collapsible) {
        handleValueChange(null);
      } else {
        handleValueChange(newValue);
      }
    },
    type
  };
  return <AccordionContext.Provider value={contextValue} data-id="element-105">
      <div className={className} data-id="element-106">{children}</div>
    </AccordionContext.Provider>;
}
// Create context for the accordion item
type AccordionItemContextValue = {
  value: string;
};
const AccordionItemContext = createContext<AccordionItemContextValue | undefined>(undefined);
// AccordionItem component
interface AccordionItemProps {
  children: React.ReactNode;
  value: string;
  className?: string;
}
export function AccordionItem({
  children,
  value,
  className = ''
}: AccordionItemProps) {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('AccordionItem must be used within an Accordion');
  }
  const isOpen = context.value === value;
  return <AccordionItemContext.Provider value={{
    value
  }} data-id="element-107">
      <div className={className} data-state={isOpen ? 'open' : 'closed'} data-id="element-108">
        {children}
      </div>
    </AccordionItemContext.Provider>;
}
// AccordionTrigger component
interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}
export function AccordionTrigger({
  children,
  className = ''
}: AccordionTriggerProps) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);
  if (!context || !itemContext) {
    throw new Error('AccordionTrigger must be used within an AccordionItem');
  }
  const isOpen = context.value === itemContext.value;
  return <button className={`flex justify-between w-full ${className}`} onClick={() => context.onValueChange(itemContext.value)} aria-expanded={isOpen} data-id="element-109">
      {children}
      <motion.div animate={{
      rotate: isOpen ? 180 : 0
    }} transition={{
      duration: 0.3
    }} data-id="element-110">
        <ChevronDownIcon className="w-6 h-6 text-[#357BEB]" data-id="element-111" />
      </motion.div>
    </button>;
}
// AccordionContent component
interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}
export function AccordionContent({
  children,
  className = ''
}: AccordionContentProps) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);
  if (!context || !itemContext) {
    throw new Error('AccordionContent must be used within an AccordionItem');
  }
  const isOpen = context.value === itemContext.value;
  return <div className={`overflow-hidden ${className}`} style={{
    display: isOpen ? 'block' : 'none'
  }} data-id="element-112">
      {children}
    </div>;
}